import { createApi } from "@reduxjs/toolkit/query/react";
import {
  ChangePasswordRequest,
  RequestChangePasswordOtpRequest,
  CheckUniqueRequest,
  CheckUniqueResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  LoginRequest,
  LoginResponse,
  RegisterUserRequest,
  RegisterUserResponse,
  ResetPasswordRequest,
  UserType,
  UpdateUserInfoRequest,
  ConfirmChangeEmailResponse,
  ConfirmChangeEmailRequest,
  UpdateUserSettingsResponse,
  UpdateUserSettingsRequest,
} from "./authApiData";
import { removeToken, setToken } from "@/helpers/cookiesHelper";
import { logout } from "../slice/authSlice";
import { userApi } from "./userApi";
import { baseQueryWithReauth } from "./baseQueryWithReauth";

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["UserSettings"],
  endpoints: (build) => ({
    registerUser: build.mutation<RegisterUserResponse, RegisterUserRequest>({
      query: (body) => ({
        url: "/auth/register",
        method: "POST",
        body,
        // headers: { "Content-Type": "application/json" },
      }),
    }),
    registerUserWithAvatar: build.mutation<any, FormData>({
      query: (formData) => ({
        url: "/auth/register",
        method: "POST",
        body: formData,
      }),
    }),
    loginUser: build.mutation<LoginResponse, LoginRequest>({
      query: (body) => ({
        url: "/auth/login",
        method: "POST",
        body,
        headers: { "Content-Type": "application/json" },
      }),
    }),
    logoutUser: build.mutation<{ message: string }, void>({
      query: () => ({
        url: "/auth/logout",
        method: "POST",
      }),
    }),
    refreshToken: build.query({
      query: () => ({
        url: "/auth/refresh",
        method: "POST",
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          setToken(data.token);
        } catch (error) {
          console.error("Failed to refresh token:", error);
          removeToken();
          dispatch(logout());
        }
      },
    }),
    checkUnique: build.mutation<CheckUniqueResponse, CheckUniqueRequest>({
      query: (body) => ({
        url: "/auth/is-unique",
        method: "POST",
        body,
      }),
    }),
    validateRegistrationCode: build.query<{ valid: boolean }, string>({
      query: (code) => ({
        url: "/registration-codes/validate",
        params: { code },
      }),
    }),
    joinWaitlist: build.mutation<{ email: string; createdAt: string }, { email: string }>({
      query: (body) => ({
        url: "/waitlist",
        method: "POST",
        body,
      }),
    }),
    updateUserInfo: build.mutation<UserType, { uid: string; data: Partial<UpdateUserInfoRequest> }>(
      {
        query: ({ data }) => ({
          url: `/users`,
          method: "PATCH",
          body: data,
        }),
        async onQueryStarted({ uid }, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            dispatch(
              userApi.util.invalidateTags([
                { type: "User", id: uid },
                { type: "UserSidebar", id: "SELF" },
              ])
            );
          } catch (e) {
            console.error("Failed to update user info:", e);
          }
        },
      }
    ),

    forgotPassword: build.mutation<ForgotPasswordResponse, ForgotPasswordRequest>({
      query: (body) => ({
        url: "/auth/forgot-password",
        method: "POST",
        body,
      }),
    }),
    validateOtp: build.mutation<any, any>({
      query: (body) => ({
        url: "/auth/validate-otp",
        method: "POST",
        body,
      }),
    }),
    resetPassword: build.mutation<any, ResetPasswordRequest>({
      query: (body) => ({
        url: "/auth/reset-password",
        method: "POST",
        body,
      }),
    }),
    requestChangePasswordOtp: build.mutation<{ message: string }, RequestChangePasswordOtpRequest>({
      query: (body) => ({
        url: "/auth/change-password/request-otp",
        method: "POST",
        body,
      }),
    }),
    changePassword: build.mutation<any, ChangePasswordRequest>({
      query: (body) => ({
        url: "/auth/change-password",
        method: "PATCH",
        body,
      }),
    }),
    validatePassword: build.mutation<any, { password: string }>({
      query: (body) => ({
        url: "/auth/validate-password",
        method: "POST",
        body,
      }),
    }),

    sendVerificationEmail: build.mutation<{ message: string }, string>({
      query: (uid) => ({
        url: "/auth/send-verification",
        method: "POST",
        body: { uid },
      }),
    }),
    resendVerification: build.mutation<{ message: string }, { email: string }>({
      query: (body) => ({
        url: "/auth/send-otp-email",
        method: "POST",
        body,
      }),
    }),
    verifyEmailToken: build.query<{ valid: boolean; tokenUid: string }, string>({
      query: (token) => ({
        url: "/auth/verify-email-token",
        params: { token },
      }),
    }),
    verifyEmailOtp: build.mutation<{ message: string }, { otp: string }>({
      query: (body) => ({
        url: "/auth/verify-email-otp",
        method: "POST",
        body,
      }),
    }),
    sendOtpEmail: build.mutation<{ message: string }, { email: string }>({
      query: (body) => ({
        url: "/auth/send-otp-email",
        method: "POST",
        body,
      }),
    }),
    changeEmail: build.mutation<{ message: string; maskedEmail: string }, { newEmail: string }>({
      query: (body) => ({
        url: "/users/email/change",
        method: "POST",
        body,
      }),
    }),
    confirmChangeEmail: build.mutation<ConfirmChangeEmailResponse, ConfirmChangeEmailRequest>({
      query: (body) => ({
        url: "/users/email/confirm",
        method: "POST",
        body,
      }),
    }),
    verifyChangeEmail: build.mutation<
      { message: string; newEmail: string },
      { requestUid: string; otp: string }
    >({
      query: (body) => ({
        url: "/users/email/verify",
        method: "POST",
        body,
      }),
    }),
    resendChangeEmail: build.mutation<
      { message: string; maskedNewEmail: string },
      { requestUid: string }
    >({
      query: (body) => ({
        url: "/users/email/resend-otp",
        method: "POST",
        body,
      }),
    }),
    updateUserSettings: build.mutation<
      UpdateUserSettingsResponse,
      Partial<UpdateUserSettingsRequest>
    >({
      query: (body) => ({
        url: "/user/settings",
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["UserSettings"],
    }),
    getUserSettings: build.query<UpdateUserSettingsResponse, void>({
      query: () => ({
        url: "/user/settings",
        method: "GET",
      }),
      providesTags: ["UserSettings"],
    }),
  }),
});

export const {
  useRegisterUserMutation,
  useRegisterUserWithAvatarMutation,
  useLoginUserMutation,
  useLogoutUserMutation,
  useCheckUniqueMutation,
  useLazyValidateRegistrationCodeQuery,
  useJoinWaitlistMutation,
  useForgotPasswordMutation,
  useValidateOtpMutation,
  useResetPasswordMutation,
  useRequestChangePasswordOtpMutation,
  useChangePasswordMutation,
  useValidatePasswordMutation,
  useUpdateUserInfoMutation,
  useRefreshTokenQuery,
  useSendVerificationEmailMutation,
  useResendVerificationMutation,
  useVerifyEmailTokenQuery,
  useVerifyEmailOtpMutation,
  useSendOtpEmailMutation,
  useChangeEmailMutation,
  useConfirmChangeEmailMutation,
  useVerifyChangeEmailMutation,
  useResendChangeEmailMutation,
  useUpdateUserSettingsMutation,
  useGetUserSettingsQuery,
} = authApi;
